import moment from "moment-timezone";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { InputTextarea, type InputTextareaProps } from "primereact/inputtextarea";
import { classNames } from "primereact/utils";
import { useTranslation } from "@i18n/hooks";
import { type RenderConfig } from "./ValidatedField";

export function DefaultTextFieldComponent({
	fieldName,
	fieldValue,
	updateField,
	isValid,
	disabled,
	onChange,
	onFocus,
	onBlur,
	placeholder,
}: RenderConfig<string>) {
	const checkIfInputFieldChanged = (e: any) => {
		if (e.target.value !== fieldValue) {
			if (onChange) {
				onChange();
			}
		}
	};

	const handleOnFocus = (e: React.FocusEvent<HTMLInputElement>) => {
		onFocus?.(e);
	};

	const handleOnBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		onBlur?.(e);
	};

	return (
		<InputText
			id={fieldName}
			name={fieldName}
			value={fieldValue}
			placeholder={placeholder}
			onChange={(e) => {
				updateField(e.target.value);
				checkIfInputFieldChanged(e);
			}}
			onFocus={handleOnFocus}
			onBlur={handleOnBlur}
			disabled={disabled}
			className={classNames({ "p-invalid": !isValid })}
		/>
	);
}

export function DefaultTextAreaComponent({
	fieldName,
	fieldValue,
	updateField,
	isValid,
	disabled,
	onChange,

	noLineBreak: _noLineBreak,

	onFocus: _onFocus,

	onBlur: _onBlur,

	canUploadFiles: _canUploadFiles,

	canDeleteFiles: _canDeleteFiles,
	...props
}: RenderConfig<string> & Partial<Omit<InputTextareaProps, "onChange" | "onFocus" | "onBlur">>) {
	return (
		<InputTextarea
			id={fieldName}
			name={fieldName}
			value={fieldValue}
			onChange={(e) => {
				updateField(e.target.value);
				if (onChange) {
					onChange();
				}
			}}
			disabled={disabled}
			className={classNames({ "p-invalid": !isValid })}
			{...props}
		/>
	);
}

export function DefaultCalendarComponent({
	fieldName,
	fieldValue,
	updateField,
	disabled,
	onChange,
}: RenderConfig<string>) {
	return (
		<Calendar
			name={fieldName}
			showTime
			hourFormat="24"
			dateFormat={"dd.mm.yy"}
			disabled={disabled}
			value={fieldValue ? moment(fieldValue.replace("[UTC]", "")).toDate() : undefined}
			onChange={(e) => {
				updateField(
					e.value ? moment(e.value as Date).format("YYYY-MM-DDTHH:mmZ") : undefined,
				);
				if (onChange) {
					onChange();
				}
			}}
		/>
	);
}

export function DefaultSwitchComponent({
	fieldName,
	fieldValue,
	updateField,
	isValid,
	disabled,
	onChange,
}: RenderConfig<boolean>) {
	return (
		<div>
			<InputSwitch
				id={fieldName}
				name={fieldName}
				checked={fieldValue ?? false}
				onChange={(e) => {
					updateField(e.target.value);
					if (onChange) {
						onChange();
					}
				}}
				disabled={disabled}
				className={classNames({ "p-invalid": !isValid })}
			/>
		</div>
	);
}

export function DefaultIntegerFieldComponent({
	fieldName,
	fieldValue,
	updateField,
	isValid,
	disabled,
	onChange,
}: RenderConfig<number>) {
	return (
		<InputNumber
			step={0}
			id={fieldName}
			name={fieldName}
			value={fieldValue}
			onChange={(e) => {
				updateField(e.value || undefined);
				if (onChange) {
					onChange();
				}
			}}
			disabled={disabled}
			className={classNames({ "p-invalid": !isValid })}
		/>
	);
}

export function DefaultPriceFieldComponent({
	fieldName,
	fieldValue,
	updateField,
	isValid,
	disabled,
	onChange,
}: RenderConfig<number>) {
	return (
		<InputNumber
			mode="currency"
			currency="EUR"
			locale="de-DE"
			id={fieldName}
			name={fieldName}
			value={fieldValue}
			minFractionDigits={2}
			maxFractionDigits={2}
			onChange={(e) => {
				updateField(e.value || undefined);
				if (onChange) {
					onChange();
				}
			}}
			disabled={disabled}
			className={classNames({ "p-invalid": !isValid })}
		/>
	);
}

export function DefaultYesNoFieldComponent({
	fieldName,
	fieldValue,
	updateField,
	isValid,
	disabled,
	onChange,
}: RenderConfig<boolean>) {
	const { t } = useTranslation("common");
	return (
		<Dropdown
			value={fieldValue}
			id={fieldName}
			name={fieldName}
			options={[
				{
					label: t("actions.yes"),
					value: true,
				},
				{
					label: t("actions.no"),
					value: false,
				},
			]}
			onChange={(e) => {
				updateField(e.value);
				if (onChange) {
					onChange();
				}
			}}
			disabled={disabled}
			className={classNames({ "p-invalid": !isValid })}
		/>
	);
}
